/**
 * Member-facing photo wall — now scope-aware.
 *
 * The legacy /api/photos global bucket is no longer used here. Photos
 * live in the chapter (or branch) Gallery the member belongs to.
 *
 * Branches:
 *   - 0 chapter memberships → show a "Request to join a chapter" panel
 *     listing available chapters with a Request button each.
 *   - 1 chapter membership   → auto-select; render the wall against that
 *     chapter's Gallery.
 *   - 2+ chapter memberships → small scope picker at the top, then wall.
 *
 * Uploads land as `pending` for read-only members and auto-approve for
 * contributors+ at the scope. Pending photos are visible only to their
 * uploader and to scope admins — they appear with a "Pending approval"
 * label on the uploader's wall until an admin moderates them via the
 * scoped admin gallery.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { Link } from 'react-router';
import { useAuth } from '@/hooks/useAuth';
import { useMyMemberships } from '@/hooks/useCan';
import {
  ApiClientError,
  chaptersApi,
  chapterJoinRequestsApi,
  galleriesApi,
  type ChapterJoinRequestRow,
  type ChapterSummary,
  type GalleryItem,
  type ScopeType,
} from '@/services/api';

interface ResolvedScope {
  scopeType: ScopeType;
  scopeId: string;
  label: string;
}

export function PhotosPage() {
  const { user } = useAuth();
  const { memberships, loading: membershipsLoading } = useMyMemberships();

  // Institution admins (e.g. super-admin) inherit effective membership on
  // every chapter in the institution. They shouldn't see the "request to
  // join" CTA on /member/photos. We materialise that here by loading the
  // chapters list when the user has an institution membership and folding
  // them into candidateScopes alongside any direct chapter memberships.
  const isInstitutionAdmin = memberships.some(
    (m) => m.scopeType === 'institution' && m.role === 'admin',
  );

  const [chaptersIndex, setChaptersIndex] = useState<Record<string, ChapterSummary>>({});

  useEffect(() => {
    if (memberships.length === 0) return;
    chaptersApi.list('navarasam').then((rows) => {
      const ix: Record<string, ChapterSummary> = {};
      for (const c of rows) ix[c.id] = c;
      setChaptersIndex(ix);
    }).catch(() => undefined);
  }, [memberships.length]);

  const candidateScopes = useMemo<ResolvedScope[]>(() => {
    const seen = new Set<string>();
    const out: ResolvedScope[] = [];
    const push = (s: ResolvedScope) => {
      const key = `${s.scopeType}:${s.scopeId}`;
      if (!seen.has(key)) {
        seen.add(key);
        out.push(s);
      }
    };
    // Direct chapter memberships first.
    for (const m of memberships) {
      if (m.scopeType === 'chapter') {
        push({ scopeType: 'chapter', scopeId: m.scopeId, label: `chapter ${m.scopeId.slice(0, 8)}…` });
      }
    }
    // For institution admins, surface every chapter in the institution.
    if (isInstitutionAdmin) {
      for (const c of Object.values(chaptersIndex)) {
        push({ scopeType: 'chapter', scopeId: c.id, label: c.displayName });
      }
    }
    return out;
  }, [memberships, isInstitutionAdmin, chaptersIndex]);

  const labeledScopes = useMemo<ResolvedScope[]>(() => {
    return candidateScopes.map((s) => {
      if (s.scopeType === 'chapter') {
        const c = chaptersIndex[s.scopeId];
        return { ...s, label: c?.displayName ?? s.label };
      }
      return s;
    });
  }, [candidateScopes, chaptersIndex]);

  const [selected, setSelected] = useState<ResolvedScope | null>(null);
  useEffect(() => {
    if (labeledScopes.length === 0) {
      if (selected) setSelected(null);
      return;
    }
    if (!selected) {
      setSelected(labeledScopes[0]!);
      return;
    }
    // Re-bind to the latest label when chaptersIndex finally loads so
    // we don't keep displaying "chapter 15B3CD80…" forever.
    const refreshed = labeledScopes.find(
      (s) => s.scopeType === selected.scopeType && s.scopeId === selected.scopeId,
    );
    if (refreshed && refreshed.label !== selected.label) {
      setSelected(refreshed);
    }
  }, [labeledScopes, selected]);

  if (membershipsLoading || !user) {
    return <div className="container mx-auto px-4 py-12 text-white/60">Loading…</div>;
  }

  if (labeledScopes.length === 0) {
    return <NoMembershipsPanel currentUserId={user.id} />;
  }

  return (
    <div className="container mx-auto px-4 py-4 space-y-3">
      {labeledScopes.length > 1 && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-white/40">Scope:</span>
          <select
            value={selected ? `${selected.scopeType}:${selected.scopeId}` : ''}
            onChange={(e) => {
              const [t, id] = e.target.value.split(':');
              const found = labeledScopes.find((s) => s.scopeType === t && s.scopeId === id);
              if (found) setSelected(found);
            }}
            className="input py-1 px-2 text-sm w-auto"
          >
            {labeledScopes.map((s) => (
              <option key={`${s.scopeType}:${s.scopeId}`} value={`${s.scopeType}:${s.scopeId}`}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
      )}
      {selected && <ScopePhotoWall scope={selected} currentUserId={user.id} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// No-membership panel: list chapters with a "Request to join" button each.
// ---------------------------------------------------------------------------

function NoMembershipsPanel({ currentUserId }: { currentUserId: string }) {
  const [chapters, setChapters] = useState<ChapterSummary[]>([]);
  const [mine, setMine] = useState<ChapterJoinRequestRow[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState('');

  const reload = useCallback(async () => {
    try {
      const [cs, m] = await Promise.all([
        chaptersApi.list('navarasam'),
        chapterJoinRequestsApi.mine(),
      ]);
      setChapters(cs);
      setMine(m);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load chapters');
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function handleRequest(chapterId: string) {
    setBusyId(chapterId);
    setError(null);
    try {
      const res = await chapterJoinRequestsApi.create(chapterId, { note: note.trim() || undefined });
      if ('alreadyMember' in res && res.alreadyMember) {
        // Could happen if memberships hydrate late — reload everything.
        window.location.reload();
        return;
      }
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send request');
    } finally {
      setBusyId(null);
    }
  }

  const pendingByChapter = useMemo(() => {
    const ix: Record<string, ChapterJoinRequestRow> = {};
    for (const r of mine) if (r.state === 'pending') ix[r.chapterId] = r;
    return ix;
  }, [mine]);

  return (
    <div className="container mx-auto px-4 py-10 max-w-3xl">
      <h1 className="text-3xl font-display font-bold text-white mb-2">Join a chapter to share photos</h1>
      <p className="text-white/60 mb-6">
        Your account isn't a member of a chapter yet. Pick a chapter below and ask its admin to let you in;
        you'll be able to upload photos and post once they approve. Already invited?{' '}
        <Link to="/login" className="link">Sign in with the invite link instead</Link>.
      </p>

      <div className="card mb-6">
        <label className="block text-sm text-white/80 mb-1">Optional message to the admin</label>
        <textarea
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={2000}
          className="input"
          placeholder="e.g. Class of 1996, Sec C — please add me"
        />
      </div>

      {error && <p className="text-red-400 mb-3">{error}</p>}

      <ul className="space-y-3">
        {chapters.map((c) => {
          const pending = pendingByChapter[c.id];
          return (
            <li key={c.id} className="card flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-white font-medium">{c.displayName}</p>
                <p className="text-xs text-white/40">
                  /{c.slug}
                  {c.batchYear ? ` · Class of ${c.batchYear}` : ''}
                  {c.location ? ` · ${c.location}` : ''}
                </p>
              </div>
              {pending ? (
                <span className="text-xs text-yellow-400 px-3 py-1 bg-yellow-500/10 border border-yellow-500/20 rounded">
                  Request pending
                </span>
              ) : (
                <button
                  type="button"
                  disabled={busyId === c.id}
                  onClick={() => handleRequest(c.id)}
                  className="btn-secondary text-sm px-4 py-1.5"
                >
                  {busyId === c.id ? 'Sending…' : 'Request to join'}
                </button>
              )}
            </li>
          );
        })}
      </ul>

      <p className="text-xs text-white/40 mt-6">
        Logged in as <span className="font-mono">{currentUserId.slice(0, 8)}…</span> — to choose a branch within
        a chapter, ask the chapter admin once they approve you.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Scoped photo wall: list + upload against the chosen gallery.
// ---------------------------------------------------------------------------

function ScopePhotoWall({ scope, currentUserId }: { scope: ResolvedScope; currentUserId: string }) {
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [flash, setFlash] = useState<{ kind: 'approved' | 'pending'; message: string } | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // Filters
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [uploaderFilter, setUploaderFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await galleriesApi.listItems(scope.scopeType, scope.scopeId, { pageSize: 200 });
      setItems(res.items);
      setTotal(res.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load photos');
    } finally {
      setLoading(false);
    }
  }, [scope.scopeType, scope.scopeId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // List of unique uploaders for the dropdown.
  const uploaders = useMemo(() => {
    const seen = new Map<string, string>();
    for (const it of items) {
      if (it.uploaderId) {
        seen.set(it.uploaderId, it.uploaderName ?? it.uploaderId.slice(0, 8));
      }
    }
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [items]);

  // Apply filters.
  const filteredItems = useMemo(() => {
    const fromTs = fromDate ? Date.parse(fromDate) : null;
    const toTs = toDate ? Date.parse(toDate) + 24 * 60 * 60 * 1000 - 1 : null;
    return items.filter((it) => {
      const t = Date.parse(it.createdAt);
      if (fromTs && t < fromTs) return false;
      if (toTs && t > toTs) return false;
      if (uploaderFilter !== 'all' && it.uploaderId !== uploaderFilter) return false;
      return true;
    });
  }, [items, fromDate, toDate, uploaderFilter]);

  // Group by upload date label ("Today", "Yesterday", or "Month YYYY").
  const groupedItems = useMemo(() => {
    const groups = new Map<string, GalleryItem[]>();
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const yest = new Date(today.getTime() - 86400000);
    const thisYear = today.getFullYear();
    for (const it of filteredItems) {
      const d = new Date(it.createdAt); d.setHours(0, 0, 0, 0);
      let label: string;
      if (d.getTime() === today.getTime()) label = 'Today';
      else if (d.getTime() === yest.getTime()) label = 'Yesterday';
      else if (d.getFullYear() === thisYear) label = d.toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
      else label = d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
      if (!groups.has(label)) groups.set(label, []);
      groups.get(label)!.push(it);
    }
    return Array.from(groups.entries());
  }, [filteredItems]);

  const hasActiveFilter = !!fromDate || !!toDate || uploaderFilter !== 'all';

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <p className="text-xs text-white/40">
          {loading
            ? 'Loading…'
            : `Showing ${filteredItems.length}${hasActiveFilter ? ` of ${items.length}` : ''} of ${total} photo${total === 1 ? '' : 's'}`}
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded border border-white/10 bg-white/5 text-white/80 hover:bg-white/10"
            title="Filter photos"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h18M6 12h12M10 20h4" />
            </svg>
            Filter
            {hasActiveFilter && <span className="ml-1 w-2 h-2 rounded-full bg-primary" />}
          </button>
          <button
            type="button"
            onClick={() => setShowUpload(true)}
            className="btn-primary inline-flex items-center gap-1.5 text-sm px-3 py-1.5"
            title="Upload a photo"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Upload
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="card flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs text-white/60 mb-1">From</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="input py-1 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-white/60 mb-1">To</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="input py-1 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-white/60 mb-1">Uploaded by</label>
            <select
              value={uploaderFilter}
              onChange={(e) => setUploaderFilter(e.target.value)}
              className="input py-1 text-sm"
            >
              <option value="all">Anyone</option>
              {uploaders.map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          </div>
          {hasActiveFilter && (
            <button
              type="button"
              onClick={() => { setFromDate(''); setToDate(''); setUploaderFilter('all'); }}
              className="text-xs text-white/70 hover:text-white underline"
            >
              Clear
            </button>
          )}
        </div>
      )}

      {flash && (
        <p className={`text-sm ${flash.kind === 'pending' ? 'text-yellow-400' : 'text-green-400'}`}>
          {flash.message}
        </p>
      )}
      {error && <p className="text-sm text-red-400">{error}</p>}

      <section className="space-y-5">
        {loading ? (
          <p className="text-white/60">Loading photos…</p>
        ) : filteredItems.length === 0 ? (
          <div className="card text-center py-10 text-white/60">
            <p className="text-lg text-white mb-1">
              {items.length === 0 ? 'No photos yet' : 'No photos match your filters'}
            </p>
            <p className="text-sm">
              {items.length === 0 ? 'Tap Upload to share the first one.' : 'Try clearing the filters.'}
            </p>
          </div>
        ) : (
          groupedItems.map(([label, group]) => (
            <div key={label}>
              <h3 className="text-xs uppercase tracking-wide text-white/40 mb-2">{label} · {group.length}</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
                {group.map((it) => {
                  const idx = filteredItems.indexOf(it);
                  return (
                    <PhotoCard
                      key={it.id}
                      item={it}
                      currentUserId={currentUserId}
                      onOpen={() => setLightboxIndex(idx)}
                    />
                  );
                })}
              </div>
            </div>
          ))
        )}
      </section>

      {showUpload && (
        <UploadPhotoModal
          scope={scope}
          onClose={() => setShowUpload(false)}
          onUploaded={(uploaded) => {
            setShowUpload(false);
            setFlash({
              kind: uploaded.status === 'pending' ? 'pending' : 'approved',
              message: uploaded.status === 'pending'
                ? 'Uploaded — waiting for chapter admin approval.'
                : 'Uploaded.',
            });
            void reload();
          }}
        />
      )}

      {lightboxIndex !== null && filteredItems[lightboxIndex] && (
        <PhotoLightbox
          items={filteredItems}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onIndexChange={setLightboxIndex}
        />
      )}
    </div>
  );
}

function UploadPhotoModal({
  scope,
  onClose,
  onUploaded,
}: {
  scope: ResolvedScope;
  onClose: () => void;
  onUploaded: (item: GalleryItem) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [caption, setCaption] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function pickFile(e: ChangeEvent<HTMLInputElement>) {
    setFile(e.target.files?.[0] ?? null);
    setError(null);
  }

  async function handleUpload() {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const res = await galleriesApi.uploadItems(scope.scopeType, scope.scopeId, [file], {
        caption: caption.trim() || undefined,
      });
      const uploaded = res.items[0];
      if (uploaded) onUploaded(uploaded);
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message);
      else setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="card max-w-lg w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-white">Upload a photo</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-white/60 hover:text-white"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-white/60 mb-1">File</label>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm"
              onChange={pickFile}
              className="text-sm text-white/80"
            />
          </div>
          <div>
            <label className="block text-xs text-white/60 mb-1">Caption (optional)</label>
            <input
              type="text"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              maxLength={2000}
              className="input"
              placeholder="What is happening in this photo?"
            />
          </div>
          {error && <p className="text-sm text-red-400">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button
              type="button"
              onClick={handleUpload}
              disabled={!file || uploading}
              className="btn-primary"
            >
              {uploading ? 'Uploading…' : 'Upload'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PhotoCard({
  item,
  currentUserId,
  onOpen,
}: {
  item: GalleryItem;
  currentUserId: string;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="bg-black/30 rounded overflow-hidden relative block text-left group"
      aria-label={item.caption || `Photo by ${item.uploaderName ?? 'member'}`}
    >
      {item.mediaKind === 'video' ? (
        <div className="aspect-square bg-black/60 flex items-center justify-center text-xs text-white/60">video</div>
      ) : (
        <img
          src={item.thumbnailUrl ?? item.blobUrl}
          alt={item.caption ?? ''}
          className="w-full aspect-square object-cover group-hover:opacity-90 transition-opacity"
          loading="lazy"
        />
      )}
      {item.status === 'pending' && (
        <span className="absolute top-1 left-1 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-yellow-500/30 text-yellow-200">
          Pending
        </span>
      )}
      {item.caption && (
        <span className="block px-2 py-1 text-xs text-white/70 truncate">{item.caption}</span>
      )}
      <span className="sr-only">{currentUserId}</span>
    </button>
  );
}

function PhotoLightbox({
  items,
  index,
  onClose,
  onIndexChange,
}: {
  items: GalleryItem[];
  index: number;
  onClose: () => void;
  onIndexChange: (next: number) => void;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const item = items[index]!;

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight' && index < items.length - 1) onIndexChange(index + 1);
      else if (e.key === 'ArrowLeft' && index > 0) onIndexChange(index - 1);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, items.length, onClose, onIndexChange]);

  async function handleFullscreen() {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      await document.exitFullscreen();
    } else {
      await el.requestFullscreen();
    }
  }

  function handleDownload() {
    // Anchor element with `download` attribute. Cross-origin SAS URLs
    // may still trigger a navigation, so we fall back to opening in
    // a new tab.
    const a = document.createElement('a');
    a.href = item.blobUrl;
    a.download = item.caption || `photo-${item.id}.jpg`;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-50 bg-black/90 flex flex-col"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex items-center justify-between px-4 py-2 text-white/80 bg-black/40">
        <div className="min-w-0">
          <p className="text-sm truncate">{item.caption ?? 'Photo'}</p>
          <p className="text-xs text-white/50">
            {item.uploaderName ?? 'Member'} · {new Date(item.createdAt).toLocaleString()} · {index + 1}/{items.length}
          </p>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={handleDownload}
            className="p-2 rounded hover:bg-white/10"
            title="Download"
            aria-label="Download"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          </button>
          <button
            type="button"
            onClick={handleFullscreen}
            className="p-2 rounded hover:bg-white/10"
            title="Full screen"
            aria-label="Toggle full screen"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M4 8V4h4M4 16v4h4M20 8V4h-4M20 16v4h-4" />
            </svg>
          </button>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded hover:bg-white/10"
            title="Close"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <div
        className="relative flex-1 flex items-center justify-center"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        {index > 0 && (
          <button
            type="button"
            onClick={() => onIndexChange(index - 1)}
            className="absolute left-2 md:left-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
            aria-label="Previous"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}
        {item.mediaKind === 'video' ? (
          <video
            src={item.blobUrl}
            controls
            className="max-h-full max-w-full"
          />
        ) : (
          <img
            src={item.blobUrl}
            alt={item.caption ?? ''}
            className="max-h-full max-w-full object-contain"
          />
        )}
        {index < items.length - 1 && (
          <button
            type="button"
            onClick={() => onIndexChange(index + 1)}
            className="absolute right-2 md:right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
            aria-label="Next"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

export default PhotosPage;
